import React, { ReactNode, useState, SyntheticEvent } from 'react';

import PropTypes from 'prop-types';

import { PhotoIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

export type ImageProps = StylableComponent<HTMLImageElement> & {
  alt?: string;
  src?: string;
  renderError?: () => ReactNode;
  onError?: (e: SyntheticEvent) => void;
  onLoad?: (e: SyntheticEvent) => void;
};

const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    { style, className, alt, src, onError, onLoad, renderError, ...props },
    ref,
  ) => {
    const theme = useTheme();
    const placeholderIconColor =
      theme.tokens?.component?.tile?.image?.['placeholder-icon-color'];

    const [error, setError] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const defaultRenderError = () => (
      <PhotoIcon
        style={{
          color: placeholderIconColor,
        }}
      />
    );

    return (
      <div
        data-testid={!error ? 'Tile__image' : 'Tile__image-placeholder'}
        css={[
          {
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        ]}
      >
        {!error && (
          <img
            ref={ref}
            className={className}
            css={[
              {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                ...(!loaded && { display: 'none' }),
              },
              style,
            ]}
            alt={alt}
            src={src}
            {...props}
            onError={(event) => {
              setError(true);
              onError?.(event);
            }}
            onLoad={(event) => {
              setLoaded(true);
              onLoad?.(event);
            }}
          />
        )}
        {error && (renderError ? renderError() : defaultRenderError())}
      </div>
    );
  },
);

Image.displayName = 'Image';

Image.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The alt text for the image */
  alt: PropTypes.string,
  /** The image URL */
  src: PropTypes.string,
  /** Function that will be executed if an error is encountered when loading image */
  renderError: PropTypes.func,
  /** Function that will be execute on image failed to load */
  onError: PropTypes.func,
  /** Function that will be execute on image load successfully*/
  onLoad: PropTypes.func,
};

export default Image;
